import * as React from 'react';
import {
  Body2,
  Button,
  makeStyles,
  OverflowItem,
  Popover,
  PopoverSurface,
  PopoverTrigger,
  shorthands,
  Tab,
  Title3,
  tokens,
  useIsOverflowItemVisible,
} from '@fluentui/react-components';
import { DismissRegular } from '@fluentui/react-icons';
import { defineMessage, useIntl } from 'react-intl';
import { useHistory } from 'react-router-dom';
import { useUpdateUserMutation, useUserQuery } from '@foundershub/api-client';
import type { NavigationTab } from '../../../components/TabNavigationTypes';
import { useUserRequired } from '../../../store';

export interface BuildWithAITabProps {
  tabs: Array<NavigationTab>;
  tab: NavigationTab;
  selectedTabId: string;
}

export function BuildWithAITab({ tabs, tab, selectedTabId, ...props }: BuildWithAITabProps) {
  const isVisible = useIsOverflowItemVisible(tab.id);
  const user = useUserRequired();
  const { isFetching: isUserFetching, isLoading: isUserLoading } = useUserQuery();
  const [updateUser, { isLoading, isError, isSuccess }] = useUpdateUserMutation();
  const hasDismissedPopover = user.user_actions_completed.includes('build-with-ai-first-time-popover');
  const [open, setOpen] = React.useState(selectedTabId !== '/ai' && !hasDismissedPopover);
  const history = useHistory();
  const classes = useStyles();
  const intl = useIntl();

  React.useEffect(() => {
    if (hasDismissedPopover) {
      setOpen(false);
    }
  }, [hasDismissedPopover]);

  const handleDismiss = async () => {
    setOpen(false);
    await updateUser({
      user_actions_completed: [...user.user_actions_completed, 'build-with-ai-first-time-popover'],
    });
  };

  React.useEffect(() => {
    // Very exhaustive check and dependency array to prevent double update calls due to different possible scenarios.
    // Clicking the popover button to Try a Template will go to /ai and dismiss the popover, but navigating to /ai does the same thing.
    // Also just navigating to the /ai tab by clicking from another tab could cause a double call as there will be 2 instances of BuildWithAITab
    // due to the Routes being defined above the LoggedIn component and the LoggedIn & TabNavigation Components being recreated for each route.
    if (
      selectedTabId === '/ai' &&
      !hasDismissedPopover &&
      !isLoading &&
      !isSuccess &&
      !isError &&
      !isUserFetching &&
      !isUserLoading
    ) {
      handleDismiss();
    }
  }, [selectedTabId, hasDismissedPopover, isLoading, isError, isSuccess, isUserFetching, isUserLoading]);

  const handleTryATemplateClick = async () => {
    await handleDismiss();
    history.push('/ai');
  };

  return (
    <Popover
      inline
      trapFocus
      withArrow
      open={open && isVisible}
      positioning={{ position: 'below', align: 'start' }}
      size="medium"
    >
      <PopoverTrigger disableButtonEnhancement>
        <OverflowItem id={tab.id} priority={tab.id === selectedTabId ? 2 : 1}>
          <Tab
            value={tab.id}
            icon={tab.id === selectedTabId ? tab.iconFilled : tab.iconRegular}
            style={tabs.indexOf(tab) === 0 ? { paddingLeft: 0 } : {}}
            {...props}
            role="tab"
          >
            {tab.name}
          </Tab>
        </OverflowItem>
      </PopoverTrigger>
      <PopoverSurface className={classes.popoverSurface}>
        <div className={classes.popoverHeader}>
          <Title3>{intl.formatMessage(popoverTitle)}</Title3>
          <Button
            type="button"
            icon={<DismissRegular />}
            appearance="transparent"
            aria-label={intl.formatMessage(popoverCloseLabel)}
            onClick={handleDismiss}
          />
        </div>
        <div>
          <Body2>{intl.formatMessage(popoverBody)}</Body2>
          <div className={classes.popoverFooter}>
            <Button type="button" appearance="primary" onClick={handleTryATemplateClick}>
              {intl.formatMessage(tryATemplateButton)}
            </Button>
          </div>
        </div>
      </PopoverSurface>
    </Popover>
  );
}

const popoverTitle = defineMessage({
  defaultMessage: 'Add the power of AI',
  description: 'Build With AI Tab Popover title',
});

const popoverBody = defineMessage({
  defaultMessage:
    'Bring your app to life with innovative, easy-to-use templates, next-gen AI integration, and popular tools.',
  description: 'Build With AI Tab Popover body text',
});

const tryATemplateButton = defineMessage({
  defaultMessage: 'Try a template',
  description: 'Text for Button on popover that opens Build with AI Tab',
});

const popoverCloseLabel = defineMessage({
  defaultMessage: 'Close popover',
  description: 'Label for close button on Build With AI Tab Popover',
});

const useStyles = makeStyles({
  popoverSurface: {
    maxWidth: '359px',
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap(tokens.spacingHorizontalM),
  },
  popoverHeader: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  popoverFooter: {
    display: 'flex',
    justifyContent: 'end',
  },
});
